import React, { useEffect, useMemo, useState } from "react";
import { money, moneyC, int, monthLabel, titleCase, seededSeries } from "./format.js";
import { IS_SAMPLE } from "./data.js";
import { Delta, RankedBars, SeedSpark, TrendChart, Empty, ErrorPanel, TableSkeleton, CatTag, BizLink, PlaceLink } from "./components.jsx";
import { dataService } from "./data.js";
import { bizPath, cityPath, countyPath } from "./slug.js";
import { countyCode, TX_COUNTY } from "./tx-counties.js";
import {
  PRO_PRICE_LABEL, PRO_PERIOD, FREE_HISTORY_MONTHS, FREE_WATCHLIST_MAX, PAYMENT_LINK, ACCOUNTS_ENABLED,
  usePlan, useAccount, signIn, signUp, signOut, activateCheckout, sessionEmail, openPortal, changePassword, adminApi, checkoutUrl, downloadCsv,
} from "./plan.js";
import ExploreTable from "./ExploreTable.jsx";

/* Every Texas county, alphabetical - the county filter lists all of them, not
 * just those that happen to have a business in the statewide top-N snapshot. */
const ALL_COUNTIES = Object.values(TX_COUNTY).slice().sort((a, b) => a.localeCompare(b));

/* ==========================================================================
 * Overview - title, filters, metric strip, ranked chart + pulse, table
 * ========================================================================*/
/* Shared filter state + derived rows/metrics for the Overview KPIs and the
 * standalone Explore table, so the two stay in lockstep. */
function useBusinessFilters(snap) {
  const [filterCity, setFilterCity] = useState("All cities");
  const [filterCounty, setFilterCounty] = useState("All counties");
  const [filterCat, setFilterCat] = useState("All categories");
  const [filterSales, setFilterSales] = useState("Any sales range");
  const [cityOptions, setCityOptions] = useState(null);  // full-universe list when supported
  const [scopeSnap, setScopeSnap] = useState(null);      // city/county-scoped rollup when picked
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeError, setScopeError] = useState(null);

  const cityActive = filterCity !== "All cities";
  const countyActive = filterCounty !== "All counties";
  const scoped = cityActive || countyActive;

  /* Full city list for the filter (every reporting city, not just the
   * statewide top-500). Falls back to snapshot-derived options when the
   * data service doesn't support it. */
  useEffect(() => {
    let live = true;
    if (dataService.cityList) {
      dataService.cityList(snap.period)
        .then((list) => live && setCityOptions(list.map(titleCase)))
        .catch(() => live && setCityOptions(null));
    }
    return () => { live = false; };
  }, [snap.period]);

  /* Picking a city and/or county re-queries the full dataset for that scope,
   * the same way the City/County pages do - small places aren't limited to the
   * statewide top-N snapshot. */
  useEffect(() => {
    let live = true;
    if (!scoped) { setScopeSnap(null); setScopeError(null); setScopeLoading(false); return; }
    setScopeLoading(true); setScopeError(null);
    const q = { limit: 400 };
    if (cityActive) q.city = filterCity;
    if (countyActive) q.county = countyCode(filterCounty);
    dataService.snapshot(snap.period, q)
      .then((d) => { if (live) { setScopeSnap(d); setScopeLoading(false); } })
      .catch((e) => { if (live) { setScopeError(e.message); setScopeLoading(false); } });
    return () => { live = false; };
  }, [filterCity, filterCounty, snap.period]); // eslint-disable-line react-hooks/exhaustive-deps

  const baseRows = scoped && scopeSnap ? scopeSnap.businesses : snap.businesses;
  const cities = useMemo(
    () => ["All cities", ...(cityOptions || [...new Set(snap.businesses.map((b) => titleCase(b.city)))].sort())],
    [snap, cityOptions]
  );
  const counties = useMemo(() => ["All counties", ...ALL_COUNTIES], []);
  const cats = useMemo(
    () => ["All categories", ...new Set(baseRows.map((b) => b.cat))].sort((a, b) => (a === "All categories" ? -1 : b === "All categories" ? 1 : a.localeCompare(b))),
    [baseRows]
  );
  const inSalesRange = (t) =>
    filterSales === "Under $100K" ? t < 100000 :
    filterSales === "$100K – $500K" ? t >= 100000 && t < 500000 :
    filterSales === "$500K – $1M" ? t >= 500000 && t < 1000000 :
    filterSales === "Over $1M" ? t >= 1000000 : true;
  const tableRows = useMemo(
    () => baseRows.filter((b) =>
      (!countyActive || b.county === filterCounty) &&
      (!cityActive || b.city?.toUpperCase() === filterCity.toUpperCase()) &&
      (filterCat === "All categories" || b.cat === filterCat) &&
      inSalesRange(b.total)
    ),
    [baseRows, filterCounty, filterCity, filterCat, filterSales]
  );
  const resetFilters = () => {
    setFilterCity("All cities"); setFilterCounty("All counties");
    setFilterCat("All categories"); setFilterSales("Any sales range");
  };

  const filtersActive = scoped || filterCat !== "All categories" || filterSales !== "Any sales range";
  const scopeLabel = cityActive ? filterCity : countyActive ? `${filterCounty} County` : "";

  /* When any filter is active, the metric strip + Top Businesses chart reflect
   * the filtered set. For a pure city/county scope we use the snapshot's real
   * scope-wide totals (uncapped); once a category/sales sub-filter is added we
   * recompute from the visible rows (prior = total / (1 + chg/100); permits
   * with no prior filing are excluded from the prior-period sum). */
  const m = useMemo(() => {
    if (!filtersActive) return snap.metrics;
    const onlyScope = scoped && filterCat === "All categories" && filterSales === "Any sales range";
    if (onlyScope && scopeSnap?.metrics) return scopeSnap.metrics;
    const rows = tableRows;
    const pct = (cur, prev) => (prev > 0 ? +(((cur - prev) / prev) * 100).toFixed(1) : null);
    const sales = rows.reduce((s, b) => s + b.total, 0);
    const locations = rows.length;
    const withPrior = rows.filter((b) => b.chg != null);
    const priorSales = withPrior.reduce((s, b) => s + b.total / (1 + b.chg / 100), 0);
    const priorCount = withPrior.length;
    const avg = locations ? Math.round(sales / locations) : 0;
    const priorAvg = priorCount ? priorSales / priorCount : 0;
    const top = rows[0];
    return {
      trackedLocations: locations,
      trackedDelta: pct(locations, priorCount),
      reportedSales: sales,
      salesDelta: pct(sales, priorSales),
      avgLocation: avg,
      avgDelta: pct(avg, priorAvg),
      highestGrossing: top ? { name: top.name, city: top.city, county: top.county, total: top.total, chg: top.chg, permit: top.permit } : null,
    };
  }, [filtersActive, scoped, scopeSnap, filterCat, filterSales, tableRows, snap.metrics]);

  return {
    filterCity, setFilterCity, filterCounty, setFilterCounty, filterCat, setFilterCat, filterSales, setFilterSales,
    cities, counties, cats, scoped, scopeLabel, baseRows, tableRows, scopeLoading, scopeError, filtersActive, m, resetFilters,
  };
}

/* Shared filter bar used by Overview and Explore. */
function FilterBar({ f, snap, months, period, onPeriod, onCity, onCounty }) {
  return (
    <div className="filter-bar">
      <span className="eyebrow">Filters</span>
      <select className="select" aria-label="City filter" value={f.filterCity} onChange={(e) => f.setFilterCity(e.target.value)}>
        {f.cities.map((c) => <option key={c}>{c}</option>)}
      </select>
      <select className="select" aria-label="County filter" value={f.filterCounty} onChange={(e) => f.setFilterCounty(e.target.value)}>
        {f.counties.map((c) => <option key={c}>{c}</option>)}
      </select>
      <select className="select" aria-label="Business type filter" value={f.filterCat} onChange={(e) => f.setFilterCat(e.target.value)}>
        {f.cats.map((c) => <option key={c}>{c}</option>)}
      </select>
      <select className="select" aria-label="Sales range filter" value={f.filterSales} onChange={(e) => f.setFilterSales(e.target.value)}>
        {["Any sales range", "Under $100K", "$100K – $500K", "$500K – $1M", "Over $1M"].map((r) => <option key={r}>{r}</option>)}
      </select>
      <select className="select" aria-label="Reporting month" value={period || ""} onChange={(e) => onPeriod(e.target.value)}>
        {(months?.months ? [...months.months].reverse() : [snap.period]).map((mo) => (
          <option key={mo} value={mo}>{monthLabel(mo, true)}{mo === months?.latest ? " · partial" : ""}</option>
        ))}
      </select>
      <button className="quiet-btn" onClick={f.resetFilters}>Reset</button>
      {f.filterCounty !== "All counties" && onCounty && (
        <button className="quiet-btn" onClick={() => onCounty(f.filterCounty)}>→ Bar Sales in {f.filterCounty} County</button>
      )}
      {f.filterCity !== "All cities" && onCity && (
        <button className="quiet-btn" onClick={() => onCity(f.filterCity)}>→ Bar Sales in {f.filterCity}</button>
      )}
    </div>
  );
}

export function Overview({ snap, onOpen, onCity, onCounty, months, period, onPeriod }) {
  const f = useBusinessFilters(snap);
  const { m, filtersActive } = f;
  const top10 = (filtersActive ? f.tableRows : snap.businesses).slice(0, 10);
  const busy = f.scopeLoading;

  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">TEXAS · {monthLabel(snap.period).toUpperCase()} · {IS_SAMPLE ? "SAMPLE DATA" : "LIVE DATA"}</div>
        <h1 className="page-title">Texas Bar Sales</h1>
        <p className="page-desc">
          Reported mixed-beverage (bar) sales for every tracked bar, restaurant, club and hotel in
          Texas: liquor, wine, beer and cover charge, reconciled monthly against TABC permits.
        </p>
      </div>

      <FilterBar f={f} snap={snap} months={months} period={period} onPeriod={onPeriod} onCity={onCity} onCounty={onCounty} />

      <div className="metric-strip">
        <Metric label="Tracked Locations" value={int(m.trackedLocations)} chg={m.trackedDelta} vs={m.trackedDelta == null ? null : int(m.trackedLocations / (1 + m.trackedDelta / 100))} sparkKey="locs" raw={m.trackedLocations} />
        <Metric label="Reported Sales" value={money(m.reportedSales)} chg={m.salesDelta} vs={m.salesDelta == null ? null : money(m.reportedSales / (1 + m.salesDelta / 100))} sparkKey="sales" raw={m.reportedSales} />
        <Metric label="Avg Location Sales" value={money(m.avgLocation)} chg={m.avgDelta} vs={m.avgDelta == null ? null : money(m.avgLocation / (1 + m.avgDelta / 100))} sparkKey="avg" raw={m.avgLocation} />
        <div className="metric-cell">
          <div className="eyebrow metric-label">Highest Grossing</div>
          <div className="metric-value-row">
            <span className="metric-value">{m.highestGrossing ? money(m.highestGrossing.total) : "–"}</span>
            {m.highestGrossing && <SeedSpark id="hg" value={m.highestGrossing.total} chg={m.highestGrossing.chg} />}
          </div>
          {m.highestGrossing && (
            <div className="metric-foot">
              <Delta v={m.highestGrossing.chg} />
              <button className="quiet-btn" style={{ padding: "0 2px" }} onClick={() => onOpen(m.highestGrossing)}>
                {titleCase(m.highestGrossing.name)}
              </button>
              · {titleCase(m.highestGrossing.city)}
            </div>
          )}
        </div>
      </div>

      <div className="section chart-pulse-grid">
        <div className="panel panel-pad">
          <div className="section-head" style={{ marginBottom: 18 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Top Businesses by Total Sales</h2>
              <div className="section-sub">
                {monthLabel(snap.period)}{filtersActive ? " · filtered" : ""} · click any bar to open the business profile
              </div>
            </div>
            <span className="eyebrow right">Top 10</span>
          </div>
          {busy ? (
            <div style={{ padding: "8px 0" }}><TableSkeleton rows={6} /></div>
          ) : top10.length ? (
            <RankedBars items={top10} onOpen={onOpen} />
          ) : (
            <Empty title="No businesses match">Loosen or reset the filters to see results.</Empty>
          )}
        </div>

        <div className="panel panel-pad">
          <div className="section-head" style={{ marginBottom: 8 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Market Pulse</h2>
              <div className="section-sub">Fastest-growing Texas metros</div>
            </div>
            <span className="eyebrow right">MoM</span>
          </div>
          {snap.pulse.map((p, i) => {
            const cc = snap.businesses.find((x) => x.city?.toUpperCase() === p.city?.toUpperCase())?.county;
            const row = (
              <>
                <span className="pulse-rank">{String(i + 1).padStart(2, "0")}</span>
                <span>
                  <div className="pulse-city">{titleCase(p.city)}</div>
                  <div className="pulse-sub num">{money(p.total)} reported</div>
                </span>
                <Delta v={p.chg} />
              </>
            );
            return cc
              ? <PlaceLink key={p.city} className="pulse-row" href={cityPath(cc, p.city)} onNav={() => onCity(p.city, cc)}>{row}</PlaceLink>
              : <button className="pulse-row" key={p.city} onClick={() => onCity(p.city)}>{row}</button>;
          })}
        </div>
      </div>

      <div className="page-foot">
        <span className="sample-badge">{IS_SAMPLE ? "Sample" : "Live"}</span>
        <span>
          {IS_SAMPLE
            ? "Prototype figures shown. Production data is sourced from the Texas Comptroller mixed-beverage gross receipts dataset and matched to TABC permit records."
            : "Figures are gross mixed-beverage receipts as filed with the Texas Comptroller (dataset naix-2893), matched to TABC permits and aggregated per reporting month. Coverage follows each permit's reporting obligation."}
        </span>
      </div>
    </div>
  );
}

function Metric({ label, value, chg, vs, sparkKey, raw }) {
  return (
    <div className="metric-cell">
      <div className="eyebrow metric-label">{label}</div>
      <div className="metric-value-row">
        <span className="metric-value">{value}</span>
        <SeedSpark id={sparkKey} value={raw} chg={chg} />
      </div>
      <div className="metric-foot">
        <Delta v={chg} /> {vs == null ? "no prior-period comparison" : `vs ${vs}`}
      </div>
    </div>
  );
}

/* Mobile stacked-card fallback for the simple Top-Businesses tables. Those
 * tables use .table-scroll, which is hidden below 680px, so without this the
 * City/County top-business lists disappear on phones. */
function TopBizCards({ rows, onOpen, showCity = false }) {
  return (
    <div className="stack-cards">
      {rows.map((b) => (
        <BizLink key={b.id} b={b} onOpen={onOpen} className="stack-card">
          <div className="stack-head">
            <span>
              <span className="b-name" style={{ fontWeight: 700, color: "var(--ink)" }}>{titleCase(b.name)}</span>
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <CatTag cat={b.cat} />
                {showCity && <span className="b-sub" style={{ fontSize: 12, color: "var(--muted-2)" }}>{titleCase(b.city)}</span>}
              </div>
            </span>
            <span style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 700, color: "var(--ink)" }} className="num">{moneyC(b.total)}</div>
              <Delta v={b.chg} />
            </span>
          </div>
          <div className="cell-permit" style={{ marginTop: 10 }}>TABC {b.permit}</div>
        </BizLink>
      ))}
    </div>
  );
}

/* ==========================================================================
 * About & Methodology - E-E-A-T / trust page
 * ========================================================================*/
export function About() {
  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">ABOUT · METHODOLOGY</div>
        <h1 className="page-title">About Texas Bar Sales</h1>
        <p className="page-desc">
          Texas Bar Sales turns the state’s public mixed-beverage tax records into a searchable,
          month-by-month view of reported sales for every tracked bar, restaurant, club and hotel
          in Texas, down to the individual venue, city, and county.
        </p>
      </div>

      <div className="note-section">
        <h2 className="note-h">Where the data comes from</h2>
        <div className="note-body">
          <p>
            Figures are gross mixed-beverage receipts as filed with the{" "}
            <a href="https://data.texas.gov/dataset/Mixed-Beverage-Gross-Receipts/naix-2893" target="_blank" rel="noopener noreferrer">
              Texas Comptroller of Public Accounts (dataset naix-2893)
            </a>{" "}
            and matched to permit records from the{" "}
            <a href="https://www.tabc.texas.gov/" target="_blank" rel="noopener noreferrer">Texas Alcoholic Beverage Commission (TABC)</a>.
            Each permit holder reports liquor, wine, beer, and cover-charge receipts monthly; those
            filings are public record.
          </p>
        </div>
        <div className="def-card"><span className="def-key">Source</span><span>Texas Comptroller mixed-beverage gross receipts (naix-2893), matched to TABC permits.</span></div>
        <div className="def-card"><span className="def-key">Updated</span><span>Monthly, following each Comptroller data release. Deltas compare a permit’s own filings across periods.</span></div>
        <div className="def-card"><span className="def-key">Coverage</span><span>Every venue that reports mixed-beverage receipts. Category labels are inferred from the permit and name and are approximate.</span></div>
      </div>

      <div className="note-section">
        <h2 className="note-h">Methodology</h2>
        <div className="note-body">
          <p>
            Sales are aggregated per TABC permit and reporting month. City and county totals sum every
            reporting venue in that place; “tracked locations” counts distinct permits with receipts in
            the period. Month-over-month and year-over-year changes compare a permit’s own filings across
            periods. Averages exclude permits with no prior filing so growth stays honest.
          </p>
          <p>
            This site presents public data for informational purposes and is not affiliated with, or
            endorsed by, the Texas Comptroller or TABC.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
 * Pricing - Free vs Pro, Stripe checkout entry
 * ========================================================================*/
export function Pricing({ onSignIn, onAccount }) {
  const { user, plan } = useAccount();
  const cta = plan === "pro" ? (
    <button className="btn plan-cta" onClick={onAccount}>✓ You’re on Pro · Manage</button>
  ) : !PAYMENT_LINK ? (
    <div className="btn plan-cta plan-cta-soon" aria-disabled="true">Checkout opening shortly</div>
  ) : !user && ACCOUNTS_ENABLED ? (
    <button className="btn plan-cta" onClick={() => onSignIn("/pricing")}>Sign in to subscribe</button>
  ) : (
    <a className="btn plan-cta" href={checkoutUrl(user?.email)}>Subscribe with Stripe</a>
  );
  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">PRICING</div>
        <h1 className="page-title">Explore free. Go deeper with Pro.</h1>
        <p className="page-desc">
          Every page on Texas Bar Sales stays free. A free account adds a watchlist. Pro unlocks the
          analyst tools: exports, full multi-year history, and unlimited tracking.
        </p>
      </div>

      <div className="plans-grid">
        <div className="panel panel-pad plan-card">
          <div className="eyebrow">Free</div>
          <div className="plan-price">$0</div>
          <div className="plan-sub">Free account</div>
          <ul className="plan-list">
            <li>Every bar, city &amp; county page</li>
            <li>Interactive Texas sales map</li>
            <li>Rankings, search &amp; filters</li>
            <li>Trailing {FREE_HISTORY_MONTHS}-month history per venue</li>
            <li>Watchlist up to {FREE_WATCHLIST_MAX} venues, synced across devices</li>
          </ul>
          {!user && ACCOUNTS_ENABLED ? (
            <button className="btn-ghost plan-cta-free" onClick={() => onSignIn("/watchlist")}>Create a free account</button>
          ) : (
            <div className="plan-cta-free" style={{ color: "var(--muted-2)" }}>{user ? `Signed in as ${user.email}` : ""}</div>
          )}
        </div>

        <div className="panel panel-pad plan-card plan-pro">
          <div className="eyebrow" style={{ color: "#CFE3D8" }}>Pro</div>
          <div className="plan-price">{PRO_PRICE_LABEL}<span className="plan-period">{PRO_PERIOD}</span></div>
          <div className="plan-sub" style={{ color: "rgba(255,255,255,0.75)" }}>Cancel anytime from your account</div>
          <ul className="plan-list">
            <li>Everything in Free</li>
            <li><b>CSV export</b>: Explore results &amp; venue history</li>
            <li><b>Full multi-year history</b> for every venue</li>
            <li><b>Unlimited watchlist</b></li>
            <li>First access to new Pro tools</li>
          </ul>
          {cta}
        </div>
      </div>

      <div className="method-panel" style={{ marginTop: 26 }}>
        <b>How billing works.</b> Payments are processed by Stripe; we never see your card. Subscriptions
        renew monthly and can be cancelled anytime from your Account page. The underlying data is
        public record; Pro pays for the tooling, not the data.
      </div>
    </div>
  );
}

/* ==========================================================================
 * Auth form - shared by Sign in page and the Welcome (post-checkout) page
 * ========================================================================*/
function AuthForm({ initialMode = "signin", presetEmail = "", onDone }) {
  const [mode, setMode] = useState(initialMode);      // signin | signup
  const [email, setEmail] = useState(presetEmail);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (presetEmail) setEmail(presetEmail); }, [presetEmail]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const me = await (mode === "signup" ? signUp(email, pw) : signIn(email, pw));
      onDone?.(me);
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  return (
    <form className="auth-card panel panel-pad" onSubmit={submit}>
      <div className="auth-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={mode === "signin"} className={mode === "signin" ? "on" : ""} onClick={() => { setMode("signin"); setErr(""); }}>Sign in</button>
        <button type="button" role="tab" aria-selected={mode === "signup"} className={mode === "signup" ? "on" : ""} onClick={() => { setMode("signup"); setErr(""); }}>Create account</button>
      </div>
      <label className="field">
        <span>Email</span>
        <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
      </label>
      <label className="field">
        <span>Password{mode === "signup" ? " (8+ characters)" : ""}</span>
        <input type="password" autoComplete={mode === "signup" ? "new-password" : "current-password"} required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} />
      </label>
      {err && <div className="form-err" role="alert">{err}</div>}
      <button className="btn" type="submit" disabled={busy} style={{ width: "100%", justifyContent: "center" }}>
        {busy ? "One moment…" : mode === "signup" ? "Create free account" : "Sign in"}
      </button>
      <div className="auth-foot">
        {mode === "signin" ? <>New here? <button type="button" className="link-btn" onClick={() => setMode("signup")}>Create a free account</button></>
          : <>Already have an account? <button type="button" className="link-btn" onClick={() => setMode("signin")}>Sign in</button></>}
      </div>
    </form>
  );
}

/* ==========================================================================
 * Sign in page (/signin?next=/path)
 * ========================================================================*/
export function SignIn({ onDone }) {
  const { user } = useAccount();
  const next = new URLSearchParams(window.location.search).get("next") || "";
  useEffect(() => { if (user) onDone(next); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ACCOUNTS_ENABLED) {
    return <div className="container"><ErrorPanel message="Accounts are not available on this preview build." /></div>;
  }
  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">ACCOUNT</div>
        <h1 className="page-title">Sign in to Texas Bar Sales</h1>
        <p className="page-desc">
          A free account keeps your watchlist in sync across devices. Pro subscribers use the same
          sign-in to unlock exports and full history everywhere.
        </p>
      </div>
      <AuthForm initialMode={next === "/watchlist" ? "signup" : "signin"} onDone={() => onDone(next)} />
    </div>
  );
}

/* ==========================================================================
 * Welcome - Stripe checkout success landing (attaches Pro to the account)
 * ========================================================================*/
export function Welcome({ onExplore, onAccount }) {
  const { user } = useAccount();
  const sid = useMemo(() => new URLSearchParams(window.location.search).get("session_id"), []);
  const [state, setState] = useState(sid ? "working" : "error");   // working | need-auth | ok | error
  const [msg, setMsg] = useState(sid ? "" : "Missing checkout session. If you just subscribed, open the link from your Stripe receipt.");
  const [preset, setPreset] = useState("");

  useEffect(() => {
    if (!sid) return;
    let live = true;
    if (!user) {
      sessionEmail(sid).then((d) => { if (live) { setPreset(d.email || ""); setState("need-auth"); } })
        .catch((e) => { if (live) { setState("error"); setMsg(e.message); } });
      return () => { live = false; };
    }
    setState("working");
    activateCheckout(sid)
      .then(() => live && setState("ok"))
      .catch((e) => { if (live) { setState("error"); setMsg(e.message); } });
    return () => { live = false; };
  }, [sid, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">WELCOME TO PRO</div>
        <h1 className="page-title">{state === "ok" ? "You’re in. 🥃" : "Thanks for subscribing"}</h1>
        <p className="page-desc">
          {state === "ok" ? "Pro is active on your account: CSV exports, full venue history, and unlimited watchlists are unlocked everywhere you sign in."
            : state === "need-auth" ? "One last step: sign in or create your account so Pro follows you to every device."
            : state === "working" ? "Confirming your payment with Stripe…" : ""}
        </p>
      </div>
      {state === "error" && <ErrorPanel message={msg} />}
      {state === "need-auth" && <AuthForm initialMode="signup" presetEmail={preset} />}
      {state === "ok" && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn" onClick={onExplore}>Start exploring</button>
          <button className="btn-ghost" onClick={onAccount}>View account</button>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
 * Account - plan, billing, password, sign out
 * ========================================================================*/
export function Account({ onSignIn, onPricing, onWatch, onAdmin, onSignedOut }) {
  const { user, plan } = useAccount();
  const [cur, setCur] = useState(""); const [nxt, setNxt] = useState("");
  const [pwMsg, setPwMsg] = useState(""); const [busy, setBusy] = useState(false);
  const [portalErr, setPortalErr] = useState("");
  useEffect(() => { if (ACCOUNTS_ENABLED && !user) onSignIn("/account"); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!user) return <div className="container" aria-busy="true"><div className="method-panel">Loading your account…</div></div>;

  const changePw = async (e) => {
    e.preventDefault(); setBusy(true); setPwMsg("");
    try { await changePassword(cur, nxt); setPwMsg("Password updated."); setCur(""); setNxt(""); }
    catch (ex) { setPwMsg(ex.message); } finally { setBusy(false); }
  };
  const portal = async () => {
    setPortalErr("");
    try { await openPortal(); } catch (ex) { setPortalErr(ex.message); }
  };

  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">ACCOUNT</div>
        <h1 className="page-title">Your account</h1>
        <p className="page-desc">{user.email}</p>
      </div>

      <div className="account-grid">
        <div className="panel panel-pad">
          <div className="eyebrow">Plan</div>
          <div className="plan-line">
            <span className={`plan-badge ${plan}`}>{plan === "pro" ? "PRO" : "FREE"}</span>
            <span>{plan === "pro" ? `${PRO_PRICE_LABEL}${PRO_PERIOD}, billed by Stripe` : `Watchlist up to ${FREE_WATCHLIST_MAX} venues, ${FREE_HISTORY_MONTHS}-month history`}</span>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
            {user.hasBilling ? (
              <button className="btn" onClick={portal}>Manage billing &amp; cancel</button>
            ) : plan !== "pro" ? (
              <button className="btn" onClick={onPricing}>Upgrade to Pro</button>
            ) : null}
            <button className="btn-ghost" onClick={onWatch}>Watchlist ({(user.watchlist || []).length}{user.watchlistMax ? ` of ${user.watchlistMax}` : ""})</button>
          </div>
          {user.override === "pro" && !user.hasBilling && <div className="form-note">Pro access granted by an administrator.</div>}
          {portalErr && <div className="form-err">{portalErr}</div>}
        </div>

        <form className="panel panel-pad" onSubmit={changePw}>
          <div className="eyebrow">Password</div>
          <label className="field"><span>Current password</span><input type="password" autoComplete="current-password" required value={cur} onChange={(e) => setCur(e.target.value)} /></label>
          <label className="field"><span>New password (8+ characters)</span><input type="password" autoComplete="new-password" required minLength={8} value={nxt} onChange={(e) => setNxt(e.target.value)} /></label>
          {pwMsg && <div className={pwMsg === "Password updated." ? "form-note" : "form-err"}>{pwMsg}</div>}
          <button className="btn-ghost" type="submit" disabled={busy}>Update password</button>
        </form>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 18 }}>
        {user.admin && <button className="btn-ghost" onClick={onAdmin}>Admin portal</button>}
        <button className="btn-ghost" onClick={() => { signOut(); onSignedOut(); }}>Sign out</button>
      </div>
    </div>
  );
}

/* ==========================================================================
 * Admin portal - list accounts, comp or revoke Pro, delete
 * ========================================================================*/
export function Admin({ onSignIn, onHome }) {
  const { user } = useAccount();
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => adminApi.users().then((d) => { setUsers(d.users); setErr(""); }).catch((e) => setErr(e.message));
  useEffect(() => { if (ACCOUNTS_ENABLED && !user) onSignIn("/admin"); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (user?.admin) load(); }, [user?.admin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return <div className="container" aria-busy="true"><div className="method-panel">Loading…</div></div>;
  if (!user.admin) return <div className="container"><ErrorPanel message="This page is for administrators." /><button className="btn-ghost" onClick={onHome}>Back home</button></div>;

  const act = async (email, patch, label) => {
    setBusy(email + label);
    try {
      await adminApi.update(email, patch);
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(""); }
  };
  const rows = (users || []).filter((u) => !q || u.email.includes(q.toLowerCase()));
  const pro = (users || []).filter((u) => u.plan === "pro").length;

  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">ADMIN</div>
        <h1 className="page-title">Subscriptions</h1>
        <p className="page-desc">{users ? `${int(users.length)} accounts · ${int(pro)} on Pro` : "Loading accounts…"}</p>
      </div>
      <div className="filter-bar" style={{ marginBottom: 14 }}>
        <input className="search-input" style={{ maxWidth: 320, paddingLeft: 13 }} placeholder="Filter by email" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="quiet-btn right" onClick={load}>↻ Refresh</button>
      </div>
      {err && <div className="form-err" style={{ marginBottom: 12 }}>{err}</div>}
      <div className="table-scroll">
        <table className="data admin-table" style={{ minWidth: 820 }}>
          <thead><tr><th>Email</th><th>Plan</th><th>Billing</th><th>Override</th><th className="num">Watchlist</th><th>Joined</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.email}>
                <td style={{ fontWeight: 600, color: "var(--ink)" }}>{u.email}{u.admin && <span className="pro-tag" style={{ background: "var(--secondary)" }}>ADMIN</span>}</td>
                <td><span className={`plan-badge ${u.plan}`}>{u.plan.toUpperCase()}</span></td>
                <td className="mono-cell">{u.customer || "none"}</td>
                <td>
                  <select className="select" value={u.override || ""} disabled={!!busy} onChange={(e) => act(u.email, { override: e.target.value || null }, "ov")}>
                    <option value="">Stripe decides</option>
                    <option value="pro">Force Pro</option>
                    <option value="free">Force Free</option>
                  </select>
                </td>
                <td className="num">{u.watchlistCount}</td>
                <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</td>
                <td>
                  {u.email !== user.email && (
                    <button className="quiet-btn" style={{ color: "var(--neg)" }} disabled={!!busy}
                      onClick={() => { if (window.confirm(`Delete ${u.email}? This cannot be undone.`)) act(u.email, { delete: true }, "del"); }}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users && rows.length === 0 && <tr><td colSpan={7} style={{ color: "var(--muted-2)" }}>No accounts match.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="method-panel" style={{ marginTop: 16 }}>
        <b>Override</b> forces a plan regardless of Stripe (comp a Pro account, or shut one off). Leave on
        "Stripe decides" for normal paying customers. Deleting an account removes its watchlist and
        sign-in; it does not cancel a Stripe subscription, do that in Stripe.
      </div>
    </div>
  );
}

/* ==========================================================================
 * Cities directory - top Texas markets ranked by reported sales
 * ========================================================================*/
export function Cities({ period, months, onPeriod, onCity }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let live = true;
    if (!period || !dataService.cityStats) return;
    setRows(null); setError(null);
    dataService.cityStats(period, 60)
      .then((d) => live && setRows(d))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [period, retry]);

  const sum = useMemo(() => (rows || []).reduce((s, r) => s + r.total, 0), [rows]);

  return (
    <div className="container">
      <div className="title-block title-row">
        <div className="title-main">
          <div className="eyebrow accent">TEXAS · {monthLabel(period).toUpperCase()} · {IS_SAMPLE ? "SAMPLE DATA" : "LIVE DATA"}</div>
          <h1 className="page-title">Texas Cities by Bar Sales</h1>
          <p className="page-desc">
            Every major Texas market ranked by reported mixed-beverage (bar) sales. Click a city for
            its full report: top venues, categories, and the monthly trend.
          </p>
        </div>
        <span className="period-btn">
          <span className="eyebrow">Period</span>
          <span className="val">{monthLabel(period)}</span>
          <select aria-label="Reporting period" value={period || ""} onChange={(e) => onPeriod(e.target.value)}>
            {(months?.months ? [...months.months].reverse() : [period]).map((m) => (
              <option key={m} value={m}>{monthLabel(m)}{m === months?.latest ? " · partial" : ""}</option>
            ))}
          </select>
        </span>
      </div>

      {error ? (
        <ErrorPanel message={error} onRetry={() => setRetry((r) => r + 1)} />
      ) : !rows ? (
        <TableSkeleton />
      ) : (
        <>
          <div className="section-sub" style={{ margin: "0 0 14px" }}>
            Top {int(rows.length)} cities · {money(sum)} combined reported sales in {monthLabel(period)}
          </div>
          <div className="table-scroll">
            <table className="data" style={{ minWidth: 640 }}>
              <thead>
                <tr><th>#</th><th>City</th><th>County</th><th className="num">Tracked Venues</th><th className="num">Reported Sales</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.city} className="clickable" tabIndex={0}
                    onClick={() => onCity(r.city, r.county)}
                    onKeyDown={(e) => e.key === "Enter" && onCity(r.city, r.county)}>
                    <td className="cell-rank">{String(i + 1).padStart(2, "0")}</td>
                    <td className="cell-biz">
                      <PlaceLink className="b-name" href={cityPath(r.county, r.city)} onNav={() => onCity(r.city, r.county)}>
                        {titleCase(r.city)}
                      </PlaceLink>
                    </td>
                    <td>{r.county}</td>
                    <td className="num">{int(r.venues)}</td>
                    <td className="num strong">{moneyC(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="stack-cards">
            {rows.map((r, i) => (
              <PlaceLink key={r.city} className="stack-card" href={cityPath(r.county, r.city)} onNav={() => onCity(r.city, r.county)}>
                <div className="stack-head">
                  <span>
                    <span className="b-name" style={{ fontWeight: 700, color: "var(--ink)" }}>{String(i + 1).padStart(2, "0")} · {titleCase(r.city)}</span>
                    <div className="b-sub" style={{ fontSize: 13, color: "var(--muted-2)", marginTop: 4 }}>{r.county} County · {int(r.venues)} venues</div>
                  </span>
                  <span className="num" style={{ fontWeight: 700, color: "var(--ink)" }}>{money(r.total)}</span>
                </div>
              </PlaceLink>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ==========================================================================
 * Explore - full filterable/searchable business table on its own page
 * ========================================================================*/
export function Explore({ snap, onOpen, onCity, onCounty, months, period, onPeriod, onUpgrade }) {
  const f = useBusinessFilters(snap);
  const plan = usePlan();
  const exportCsv = () => {
    if (plan !== "pro") { onUpgrade(); return; }
    downloadCsv(
      `texas-bar-sales-${snap.period}.csv`,
      ["Business", "Address", "City", "County", "Category", "TABC Permit", "Liquor", "Wine", "Beer", "Cover", "Total Sales", "MoM %"],
      f.tableRows.map((b) => [
        b.name, b.addr, b.city, b.county, b.cat, b.permit,
        Math.round(b.total * (b.mix?.l || 0)), Math.round(b.total * (b.mix?.w || 0)),
        Math.round(b.total * (b.mix?.b || 0)), Math.round(b.total * (b.mix?.c || 0)),
        b.total, b.chg ?? "",
      ])
    );
  };
  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">TEXAS · {monthLabel(snap.period).toUpperCase()} · {IS_SAMPLE ? "SAMPLE DATA" : "LIVE DATA"}</div>
        <h1 className="page-title">Explore Businesses</h1>
        <p className="page-desc">
          Filter and search every tracked bar, restaurant, club and hotel in Texas by city, county,
          category, and sales range. Click any row to open its profile.
        </p>
      </div>

      <FilterBar f={f} snap={snap} months={months} period={period} onPeriod={onPeriod} onCity={onCity} onCounty={onCounty} />

      <div className="section">
        <div className="section-head">
          <div>
            <h2 className="section-h2">{f.scopeLabel ? `${f.scopeLabel} ` : ""}Results</h2>
            <div className="section-sub">
              {f.tableRows.length === f.baseRows.length
                ? `${int(f.tableRows.length)} ${f.scopeLabel ? `${f.scopeLabel} ` : ""}locations`
                : `${int(f.tableRows.length)} of ${int(f.baseRows.length)} tracked ${f.scopeLabel ? `${f.scopeLabel} ` : ""}locations match`} · sorted by total sales · click a row to open its profile
            </div>
          </div>
          <button className="quiet-btn right" onClick={exportCsv}>
            ⤓ Export CSV{plan !== "pro" && <span className="pro-tag">PRO</span>}
          </button>
        </div>
        {f.scopeError ? (
          <ErrorPanel message={f.scopeError} onRetry={() => { f.resetFilters(); }} />
        ) : f.scopeLoading ? (
          <TableSkeleton />
        ) : (
          <ExploreTable
            businesses={f.tableRows} onOpen={onOpen}
            emptyTitle="No reports match" emptyHint="Try another place or reset the filters."
          />
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
 * Rankings - leaderboard grid
 * ========================================================================*/
export function Rankings({ snap, onOpen, onCity }) {
  const byTotal = snap.businesses;
  const byGrowth = useMemo(() => [...snap.businesses].sort((a, b) => b.chg - a.chg), [snap]);
  const cityBoards = useMemo(() => {
    const order = snap.pulse.map((p) => p.city);
    return order.slice(0, 4).map((city) => ({
      city,
      items: snap.businesses.filter((b) => b.city === city).slice(0, 5),
    })).filter((g) => g.items.length);
  }, [snap]);

  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">TEXAS · {monthLabel(snap.period).toUpperCase()} · {IS_SAMPLE ? "SAMPLE DATA" : "LIVE DATA"}</div>
        <h1 className="page-title">Rankings</h1>
        <p className="page-desc">
          Leaderboards across the tracked universe: statewide leaders, fastest movers, and the
          strongest venues in each major metro. Click any entry to open its profile.
        </p>
      </div>

      <div className="boards-grid">
        <Board title="Top Businesses in Texas" metric="Total Sales">
          {byTotal.slice(0, 8).map((b, i) => (
            <BoardRow key={b.id} i={i} name={titleCase(b.name)} sub={`${titleCase(b.city)} · ${b.cat}`}
              val={money(b.total)} chg={b.chg} href={bizPath(b)} onClick={() => onOpen(b)} />
          ))}
        </Board>
        <Board title="Fastest Growing" metric="Month / Month">
          {byGrowth.slice(0, 8).map((b, i) => (
            <BoardRow key={b.id} i={i} name={titleCase(b.name)} sub={`${titleCase(b.city)} · ${b.cat}`}
              val={money(b.total)} chg={b.chg} href={bizPath(b)} onClick={() => onOpen(b)} />
          ))}
        </Board>
        <Board title="Leading Markets" metric="Total Sales">
          {snap.pulse.map((p, i) => (
            <BoardRow key={p.city} i={i} name={titleCase(p.city)} sub="Metro market · Texas"
              val={money(p.total)} chg={p.chg} onClick={() => onCity(p.city)} />
          ))}
        </Board>
        {cityBoards.map((g) => (
          <Board key={g.city} title={`Top in ${titleCase(g.city)}`} metric="Total Sales">
            {g.items.map((b, i) => (
              <BoardRow key={b.id} i={i} name={titleCase(b.name)} sub={`${titleCase(b.city)} · ${b.cat}`}
                val={money(b.total)} chg={b.chg} onClick={() => onOpen(b)} />
            ))}
          </Board>
        ))}
      </div>
    </div>
  );
}

function Board({ title, metric, children }) {
  return (
    <div className="panel">
      <div className="board-head">
        <h2 className="board-title">{title}</h2>
        <span className="eyebrow">{metric}</span>
      </div>
      {children}
    </div>
  );
}

function BoardRow({ i, name, sub, val, chg, onClick, href }) {
  const inner = (
    <>
      <span className={`board-rank ${i === 0 ? "first" : ""}`}>{String(i + 1).padStart(2, "0")}</span>
      <span style={{ minWidth: 0 }}>
        <div className="board-name" title={name}>{name}</div>
        <div className="board-sub">{sub}</div>
      </span>
      <span>
        <div className="board-val">{val}</div>
        <div style={{ textAlign: "right" }}><Delta v={chg} /></div>
      </span>
    </>
  );
  const mod = (e) => e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;
  return href
    ? <a className="board-row" href={href} onClick={(e) => { if (mod(e)) return; e.preventDefault(); onClick(); }}>{inner}</a>
    : <button className="board-row" onClick={onClick}>{inner}</button>;
}

/* ==========================================================================
 * City Market page
 * ========================================================================*/
const CAT_BUCKETS = [
  ["Bars & Taverns", ["Bar", "Lounge"]],
  ["Restaurants", ["Restaurant"]],
  ["Live Music & Clubs", ["Live Music", "Nightclub", "Private Club"]],
  ["Hotels & Wine Bars", ["Hotel", "Wine Bar", "Brewery"]],
];
const CITY_RAMP = ["#167C5B", "#3E9A7C", "#6AB59C", "#97CCBD"];

export function City({ snap, city, period, onOpen, onCounty, following, onFollow }) {
  const [citySnap, setCitySnap] = useState(null);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);

  /* City-scoped snapshot: the statewide top-500 under-covers smaller metros. */
  useEffect(() => {
    let live = true;
    setCitySnap(null); setError(null);
    dataService.snapshot(period, { city: titleCase(city), limit: 200 })
      .then((d) => live && setCitySnap(d))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [city, period, retry]);

  const rows = useMemo(() => citySnap?.businesses ?? [], [citySnap]);
  const stats = useMemo(() => {
    /* Prefer the scope-wide metrics returned by the snapshot (true count/sum
     * over every venue in the city, not just the capped top-N rows fetched). */
    const m = citySnap?.metrics;
    if (m && m.trackedLocations) {
      const priorSales = m.salesDelta ? m.reportedSales / (1 + m.salesDelta / 100) : m.reportedSales;
      const avgPrior = m.avgDelta ? m.avgLocation / (1 + m.avgDelta / 100) : m.avgLocation;
      return {
        total: m.reportedSales, chg: m.salesDelta, prior: priorSales,
        count: m.trackedLocations, avg: m.avgLocation, avgPrior,
        top: m.highestGrossing || rows[0],
      };
    }
    const total = rows.reduce((s, b) => s + b.total, 0);
    const prior = rows.reduce((s, b) => s + (b.chg > -100 ? b.total / (1 + b.chg / 100) : b.total), 0);
    const pulse = snap.pulse.find((p) => p.city?.toUpperCase() === city?.toUpperCase());
    return {
      total,
      chg: pulse ? pulse.chg : prior > 0 ? +(((total - prior) / prior) * 100).toFixed(1) : 0,
      prior,
      count: rows.length,
      avg: rows.length ? Math.round(total / rows.length) : 0,
      avgPrior: rows.length ? prior / rows.length : 0,
      top: rows[0],
    };
  }, [rows, citySnap, snap, city]);

  /* Real trailing-12-month series for the city when the snapshot provides it;
   * otherwise fall back to an illustrative seeded series. */
  const trendSeries = useMemo(() => {
    if (citySnap?.trend?.length) return citySnap.trend;
    const vals = seededSeries(`city:${city}:${snap.period}`, stats.total, 12, 0.035);
    let [y, m] = snap.period.split("-").map(Number);
    const out = [];
    for (let i = 11; i >= 0; i--) {
      let mm = m - i, yy = y;
      while (mm < 1) { mm += 12; yy--; }
      out.push({ m: `${yy}-${String(mm).padStart(2, "0")}`, v: Math.round(vals[11 - i]) });
    }
    return out;
  }, [city, citySnap, snap, stats.total]);
  const trendReal = !!citySnap?.trend?.length;

  const buckets = useMemo(() => {
    const agg = CAT_BUCKETS.map(([label, cats]) => ({
      label,
      total: rows.filter((b) => cats.includes(b.cat)).reduce((s, b) => s + b.total, 0),
    })).filter((x) => x.total > 0).sort((a, b) => b.total - a.total);
    const sum = agg.reduce((s, x) => s + x.total, 0) || 1;
    return agg.map((x) => ({ ...x, share: x.total / sum }));
  }, [rows]);

  const county = rows[0]?.county;

  if (error) {
    return (
      <div className="container">
        <div className="title-block">
          <div className="eyebrow accent">UNITED STATES › TEXAS › METRO MARKET</div>
          <h1 className="page-title">{titleCase(city)}</h1>
        </div>
        <ErrorPanel message={error} onRetry={() => setRetry((r) => r + 1)} />
      </div>
    );
  }
  if (!citySnap) {
    return (
      <div className="container" aria-busy="true">
        <div className="title-block">
          <div className="eyebrow accent">UNITED STATES › TEXAS › METRO MARKET</div>
          <h1 className="page-title">{titleCase(city)}</h1>
        </div>
        <TableSkeleton />
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="container">
        <div className="title-block">
          <div className="eyebrow accent">UNITED STATES › TEXAS › METRO MARKET</div>
          <h1 className="page-title">{titleCase(city)}</h1>
        </div>
        <Empty title={`No reports in ${titleCase(city)}`}>No tracked locations reported for {monthLabel(snap.period)}.</Empty>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="title-block title-row">
        <div className="title-main">
          <div className="eyebrow accent">
            UNITED STATES › TEXAS ›{" "}
            {county && onCounty
              ? <PlaceLink className="crumb-seg" href={countyPath(county)} onNav={() => onCounty(county)}>{county.toUpperCase()} COUNTY</PlaceLink>
              : "METRO MARKET"}
          </div>
          <h1 className="page-title" style={{ fontSize: 36 }}>Bar Sales in {titleCase(city)}</h1>
          <p className="page-desc">
            {county} County, Texas · {monthLabel(snap.period)}. Reported mixed-beverage (bar) sales across
            {" "}{titleCase(city)}’s tracked bars, restaurants, clubs and hotels, a read on local nightlife
            and hospitality demand.
          </p>
        </div>
        <button className={`btn ${following ? "following" : ""}`} onClick={onFollow}>
          {following ? "✓ Following market" : "+ Follow market"}
        </button>
      </div>

      <div className="metric-strip">
        <div className="metric-cell">
          <div className="eyebrow metric-label">Total Reported Sales</div>
          <div className="metric-value">{money(stats.total)}</div>
          <div className="metric-foot"><Delta v={stats.chg} /> vs {money(stats.prior)} last month</div>
        </div>
        <div className="metric-cell">
          <div className="eyebrow metric-label">Tracked Locations</div>
          <div className="metric-value">{int(stats.count)}</div>
          <div className="metric-foot">reporting in {monthLabel(snap.period, true)}</div>
        </div>
        <div className="metric-cell">
          <div className="eyebrow metric-label">Avg Location Sales</div>
          <div className="metric-value">{money(stats.avg)}</div>
          <div className="metric-foot"><Delta v={stats.avgPrior > 0 ? ((stats.avg - stats.avgPrior) / stats.avgPrior) * 100 : 0} /> vs {money(stats.avgPrior)} last month</div>
        </div>
        <div className="metric-cell">
          <div className="eyebrow metric-label">Top Performer</div>
          <div className="metric-value">{money(stats.top.total)}</div>
          <div className="metric-foot"><Delta v={stats.top.chg} /> {titleCase(stats.top.name)}</div>
        </div>
      </div>

      <div className="section two-col-1000">
        <div className="panel panel-pad">
          <div className="section-head" style={{ marginBottom: 6 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Monthly Market Trend</h2>
              <div className="section-sub">Total reported sales across all tracked {titleCase(city)} locations{trendReal ? "" : " · illustrative series"}</div>
            </div>
            <span className="eyebrow right">Trailing 12 months</span>
          </div>
          <TrendChart data={trendSeries} height={240} />
        </div>
        <div className="panel panel-pad">
          <div className="section-head" style={{ marginBottom: 16 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Top Categories</h2>
              <div className="section-sub">Share of reported sales · {monthLabel(snap.period)}</div>
            </div>
          </div>
          {buckets.map((x, i) => (
            <div className="share-row" key={x.label}>
              <div className="share-head">
                <span className="share-name">{x.label}</span>
                <span className="share-val">{money(x.total)} · {Math.round(x.share * 100)}%</span>
              </div>
              <div className="share-track">
                <div className="share-bar" style={{ width: `${x.share * 100}%`, background: CITY_RAMP[Math.min(i, 3)] }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <div>
            <h2 className="section-h2">Top Businesses in {titleCase(city)}</h2>
            <div className="section-sub">Tracked venues ranked by reported sales · click a row to open its profile</div>
          </div>
        </div>
        <div className="table-scroll">
          <table className="data" style={{ minWidth: 640 }}>
            <thead>
              <tr><th>#</th><th>Business</th><th>Permit</th><th className="num">Total Sales</th><th className="num">MoM</th></tr>
            </thead>
            <tbody>
              {rows.slice(0, 10).map((b, i) => (
                <tr key={b.id} className="clickable" tabIndex={0}
                  onClick={() => onOpen(b)}
                  onKeyDown={(e) => e.key === "Enter" && onOpen(b)}>
                  <td className="cell-rank">{String(i + 1).padStart(2, "0")}</td>
                  <td className="cell-biz">
                    <BizLink b={b} onOpen={onOpen} className="b-name">{titleCase(b.name)}</BizLink>
                    <div style={{ marginTop: 5 }}><CatTag cat={b.cat} /></div>
                  </td>
                  <td className="cell-permit">{b.permit}</td>
                  <td className="num strong">{moneyC(b.total)}</td>
                  <td className="num"><Delta v={b.chg} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TopBizCards rows={rows.slice(0, 10)} onOpen={onOpen} />
      </div>
    </div>
  );
}

/* ==========================================================================
 * County page - cities in the county + top businesses
 * ========================================================================*/
export function County({ county, period, snap, onOpen, onCity }) {
  const [countySnap, setCountySnap] = useState(null);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const code = countyCode(county);

  useEffect(() => {
    let live = true;
    setCountySnap(null); setError(null);
    dataService.snapshot(period, { county: code, limit: 400 })
      .then((d) => live && setCountySnap(d))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [code, period, retry]);

  const rows = useMemo(() => countySnap?.businesses ?? [], [countySnap]);
  const stats = useMemo(() => {
    /* Prefer scope-wide metrics (true count/sum over the whole county). */
    const m = countySnap?.metrics;
    if (m && m.trackedLocations) {
      const prior = m.salesDelta ? m.reportedSales / (1 + m.salesDelta / 100) : m.reportedSales;
      return {
        total: m.reportedSales, prior,
        chg: m.salesDelta ?? null,
        count: m.trackedLocations, avg: m.avgLocation,
        top: m.highestGrossing || rows[0],
      };
    }
    const total = rows.reduce((s, b) => s + b.total, 0);
    const withPrior = rows.filter((b) => b.chg != null && b.chg > -100);
    const prior = withPrior.reduce((s, b) => s + b.total / (1 + b.chg / 100), 0);
    return {
      total, prior,
      chg: prior > 0 ? +(((total - prior) / prior) * 100).toFixed(1) : null,
      count: rows.length,
      avg: rows.length ? Math.round(total / rows.length) : 0,
      top: rows[0],
    };
  }, [rows, countySnap]);

  const cities = useMemo(() => {
    const by = new Map();
    for (const b of rows) {
      const key = b.city;
      if (!by.has(key)) by.set(key, { city: key, total: 0, count: 0 });
      const g = by.get(key); g.total += b.total; g.count++;
    }
    return [...by.values()].sort((a, b) => b.total - a.total);
  }, [rows]);

  const trendSeries = useMemo(() => {
    if (countySnap?.trend?.length) return countySnap.trend;
    const vals = seededSeries(`county:${county}:${period}`, stats.total, 12, 0.03);
    let [y, m] = period.split("-").map(Number);
    const out = [];
    for (let i = 11; i >= 0; i--) {
      let mm = m - i, yy = y;
      while (mm < 1) { mm += 12; yy--; }
      out.push({ m: `${yy}-${String(mm).padStart(2, "0")}`, v: Math.round(vals[11 - i]) });
    }
    return out;
  }, [county, period, stats.total, countySnap]);
  const trendReal = !!countySnap?.trend?.length;

  const header = (
    <div className="title-block">
      <div className="eyebrow accent">UNITED STATES › TEXAS › COUNTY</div>
      <h1 className="page-title" style={{ fontSize: 36 }}>Bar Sales in {county} County</h1>
    </div>
  );

  if (error) return <div className="container">{header}<ErrorPanel message={error} onRetry={() => setRetry((r) => r + 1)} /></div>;
  if (!countySnap) return <div className="container" aria-busy="true">{header}<TableSkeleton /></div>;
  if (!rows.length) return <div className="container">{header}<Empty title={`No reports in ${county} County`}>No tracked locations reported for {monthLabel(period)}.</Empty></div>;

  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">UNITED STATES › TEXAS › COUNTY</div>
        <h1 className="page-title" style={{ fontSize: 36 }}>Bar Sales in {county} County</h1>
        <p className="page-desc">
          {county} County, Texas · {monthLabel(period)}. Reported mixed-beverage (bar) sales across every
          tracked venue in the county: {int(stats.count)} locations across {cities.length} cities.
        </p>
      </div>

      <div className="metric-strip">
        <div className="metric-cell">
          <div className="eyebrow metric-label">Total Reported Sales</div>
          <div className="metric-value">{money(stats.total)}</div>
          <div className="metric-foot"><Delta v={stats.chg} /> {stats.chg == null ? "no prior-month comparison" : `vs ${money(stats.prior)} last month`}</div>
        </div>
        <div className="metric-cell">
          <div className="eyebrow metric-label">Tracked Locations</div>
          <div className="metric-value">{int(stats.count)}</div>
          <div className="metric-foot">reporting in {monthLabel(period, true)}</div>
        </div>
        <div className="metric-cell">
          <div className="eyebrow metric-label">Cities Reporting</div>
          <div className="metric-value">{int(cities.length)}</div>
          <div className="metric-foot">with tracked venues</div>
        </div>
        <div className="metric-cell">
          <div className="eyebrow metric-label">Top Performer</div>
          <div className="metric-value">{money(stats.top.total)}</div>
          <div className="metric-foot"><Delta v={stats.top.chg} /> {titleCase(stats.top.name)}</div>
        </div>
      </div>

      <div className="section two-col-1000">
        <div className="panel panel-pad">
          <div className="section-head" style={{ marginBottom: 6 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Monthly County Trend</h2>
              <div className="section-sub">Total reported sales across all tracked {county} County locations{trendReal ? "" : " · illustrative series"}</div>
            </div>
            <span className="eyebrow right">Trailing 12 months</span>
          </div>
          <TrendChart data={trendSeries} height={240} />
        </div>
        <div className="panel panel-pad">
          <div className="section-head" style={{ marginBottom: 8 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Cities in {county} County</h2>
              <div className="section-sub">By reported sales · {monthLabel(period)}</div>
            </div>
          </div>
          {cities.slice(0, 8).map((c, i) => (
            <PlaceLink className="pulse-row" key={c.city} href={cityPath(county, c.city)} onNav={() => onCity(c.city, county)}>
              <span className="pulse-rank">{String(i + 1).padStart(2, "0")}</span>
              <span>
                <div className="pulse-city">{titleCase(c.city)}</div>
                <div className="pulse-sub num">{int(c.count)} tracked</div>
              </span>
              <span className="pulse-amt">{money(c.total)}</span>
            </PlaceLink>
          ))}
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <div>
            <h2 className="section-h2">Top Businesses in {county} County</h2>
            <div className="section-sub">Tracked venues ranked by reported sales · click a row to open its profile</div>
          </div>
        </div>
        <div className="table-scroll">
          <table className="data" style={{ minWidth: 680 }}>
            <thead>
              <tr><th>#</th><th>Business</th><th>City</th><th>Permit</th><th className="num">Total Sales</th><th className="num">MoM</th></tr>
            </thead>
            <tbody>
              {rows.slice(0, 15).map((b, i) => (
                <tr key={b.id} className="clickable" tabIndex={0}
                  onClick={() => onOpen(b)} onKeyDown={(e) => e.key === "Enter" && onOpen(b)}>
                  <td className="cell-rank">{String(i + 1).padStart(2, "0")}</td>
                  <td className="cell-biz">
                    <BizLink b={b} onOpen={onOpen} className="b-name">{titleCase(b.name)}</BizLink>
                    <div style={{ marginTop: 5 }}><CatTag cat={b.cat} /></div>
                  </td>
                  <td>{titleCase(b.city)}</td>
                  <td className="cell-permit">{b.permit}</td>
                  <td className="num strong">{moneyC(b.total)}</td>
                  <td className="num"><Delta v={b.chg} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TopBizCards rows={rows.slice(0, 15)} onOpen={onOpen} showCity />
      </div>
    </div>
  );
}

/* ==========================================================================
 * Watchlist
 * ========================================================================*/
export function Watchlist({ snap, watchlist, onOpen, onRemove, onExplore, onSignIn, onPricing }) {
  const { user, plan } = useAccount();
  const [extra, setExtra] = useState({});   // permit -> business for follows outside the snapshot
  const inSnap = snap.businesses.filter((b) => watchlist.has(b.permit));
  const missing = [...watchlist].filter((p) => !snap.businesses.some((b) => b.permit === p) && !extra[p]);
  useEffect(() => {
    if (!missing.length) return;
    let live = true;
    Promise.all(missing.map((p) => dataService.business(p, snap.period).then((d) => [p, d.business]).catch(() => [p, null])))
      .then((pairs) => live && setExtra((x) => ({ ...x, ...Object.fromEntries(pairs) })));
    return () => { live = false; };
  }, [missing.join(","), snap.period]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = [...inSnap, ...[...watchlist].map((p) => extra[p]).filter(Boolean)];

  return (
    <div className="container">
      <div className="title-block">
        <div className="eyebrow accent">FOLLOWED · {monthLabel(snap.period).toUpperCase()}</div>
        <h1 className="page-title">Watchlist</h1>
        <p className="page-desc">
          Businesses you follow, tracked month over month and synced to your account. Add locations
          from any profile to monitor their reported sales here.
        </p>
      </div>

      {ACCOUNTS_ENABLED && !user ? (
        <Empty title="Sign in to use your watchlist" cta="Sign in or create a free account" onCta={() => onSignIn("/watchlist")}>
          A free account follows up to {FREE_WATCHLIST_MAX} venues across all your devices. Pro follows unlimited venues.
        </Empty>
      ) : rows.length === 0 ? (
        <Empty title="Nothing followed yet" cta="Explore businesses" onCta={onExplore}>
          Open any business profile and tap “+ Watchlist” to track its reported sales here,
          month over month.
        </Empty>
      ) : (
        <>
          <div className="watch-list">
            {rows.map((b) => (
              <div key={b.permit} className="watch-row" tabIndex={0} role="button"
                onClick={() => onOpen(b)}
                onKeyDown={(e) => e.key === "Enter" && onOpen(b)}>
                <div className="watch-id">
                  <div className="watch-name">{titleCase(b.name)}</div>
                  <div className="watch-sub">{titleCase(b.city)}, {b.county} · {b.cat}</div>
                </div>
                <SeedSpark id={b.permit} value={b.total} chg={b.chg} />
                <div className="watch-num">
                  <div className="watch-total">{moneyC(b.total)}</div>
                  <Delta v={b.chg} />
                </div>
                <button
                  className="remove-btn" aria-label={`Remove ${titleCase(b.name)} from watchlist`}
                  onClick={(e) => { e.stopPropagation(); onRemove(b.permit); }}
                >✕</button>
              </div>
            ))}
          </div>
          {user && plan !== "pro" && (
            <div className="method-panel" style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <span>Following {watchlist.size} of {FREE_WATCHLIST_MAX} on the free plan.</span>
              <button className="btn" onClick={onPricing}>Unlimited watchlist · Pro</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
